<template>
  <div class="login-page-container d-flex align-items-center justify-content-center min-vh-100 bg-light view-user-login">
    <div class="login-box" style="width: 100%; max-width: 400px;">
      <b-card class="shadow">
        <b-card-body>
          <div class="row">
            <div class="col-12 text-center mb-3">
              <img
                class="logo-login-img"
                src="@/assets/img/dentcloud-logo-darkblue.png"
              />
              <h3 class="logo-caption mt-2" style="color: #38689C; font-weight: bold;">
                <span>DentCloud</span>
              </h3>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div
                class="alert alert-warning alert-dismissible fade show"
                role="alert"
                data-testid="login-error"
                v-if="statusLogin"
              >
                {{ loginErrorMessage || ERROR_MESSAGES.INVALID_CREDENTIALS_CHECK }}
                <button
                  type="button"
                  class="close"
                  data-dismiss="alert"
                  aria-label="Close"
                  @click="setStatusLogin"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>
          </div>
          <!--
            Passkey is the primary action, but the password form below stays
            fully visible: an un-enrolled user must still be able to log in
            without hunting for it. Rendered only where WebAuthn exists.
          -->
          <div v-if="passkeySupported && !mfaToken">
            <div class="row">
              <div class="col-12">
                <button
                  type="button"
                  data-testid="passkey-login"
                  class="btn btn-primary btn-block btn-sm"
                  :disabled="isLoading || isPasskeyLoading"
                  @click="submitPasskeyLogin"
                >
                  <span class="fas fa-fingerprint mr-1"></span>
                  {{
                    isPasskeyLoading
                      ? "กำลังยืนยันตัวตน..."
                      : "เข้าสู่ระบบด้วย Passkey"
                  }}
                </button>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="passkey-divider my-3"><span>หรือ</span></div>
              </div>
            </div>
          </div>

          <b-form v-if="!mfaToken" @submit.prevent="submit" autocomplete="off">
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <label for="email">อีเมลล์</label>
                  <div class="input-group">
                    <input
                      id="login-input-login-view"
                      data-testid="login-username"
                      class="form-control form-control-sm"
                      v-model.trim="loginForm.username"
                      required
                      :disabled="isLoading || isPasskeyLoading"
                      autocomplete="username"
                      maxlength="100"
                    />
                    <div class="input-group-append">
                      <div class="input-group-text">
                        <span class="fas fa-user"></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group">
                  <label>รหัสผ่าน</label>
                  <div class="input-group">
                    <input
                      id="password-input-login-view"
                      data-testid="login-password"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control form-control-sm"
                      v-model="loginForm.password"
                      required
                      :disabled="isLoading || isPasskeyLoading"
                      autocomplete="current-password"
                      maxlength="100"
                    />
                    <div class="input-group-append">
                      <div
                        class="input-group-text cursor-pointer"
                        @click="togglePassword"
                      >
                        <span
                          :class="
                            showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                          "
                        ></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <b-row class="mb-2">
              <b-col cols="12">
                <b-form-checkbox
                  size="sm"
                  v-model="loginForm.rememberMe"
                  @change="saveToLocalStorage"
                  :disabled="isLoading"
                  >จดจำฉัน</b-form-checkbox
                >
              </b-col>
            </b-row>
            <div class="row">
              <div class="col-12">
                <button
                  type="submit"
                  data-testid="login-submit"
                  class="btn btn-primary btn-block btn-sm"
                  :disabled="
                    isLoading ||
                    isPasskeyLoading ||
                    !loginForm.username ||
                    !loginForm.password
                  "
                >
                  {{ isLoading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ" }}
                </button>
              </div>
            </div>
          </b-form>
          <div class="row" v-if="!mfaToken">
            <div class="col-12">
              <span class="mb-0 mt-3 ml-auto">
                <a href="/user/forgotpassword" class="text-center"
                  >ลืมรหัสผ่าน?</a
                >
              </span>
            </div>
          </div>

          <div v-else>
            <div class="row">
              <div class="col-12 text-center mb-3">
                <h5 class="mb-1">ยืนยันตัวตนสองชั้น</h5>
                <p class="text-muted small mb-0">
                  กรอกรหัส 6 หลักจากแอปยืนยันตัวตนของคุณ
                </p>
              </div>
            </div>

            <b-form @submit.prevent="handleMfaSubmit" autocomplete="off">
              <div class="row">
                <div class="col-12">
                  <div class="form-group">
                    <label v-if="!recoveryMode" for="mfa-code-input"
                      >รหัส 6 หลัก</label
                    >
                    <input
                      v-if="!recoveryMode"
                      id="mfa-code-input"
                      ref="mfaCodeInput"
                      data-testid="mfa-code"
                      class="form-control form-control-sm text-center"
                      style="letter-spacing: 4px;"
                      inputmode="numeric"
                      maxlength="6"
                      autocomplete="one-time-code"
                      :value="mfaCode"
                      @input="onMfaCodeInput($event.target.value)"
                      :disabled="isLoading"
                    />
                    <label v-else for="mfa-recovery-input">รหัสสำรอง</label>
                    <input
                      v-if="recoveryMode"
                      id="mfa-recovery-input"
                      data-testid="mfa-recovery-code"
                      class="form-control form-control-sm"
                      v-model.trim="recoveryCode"
                      autocomplete="off"
                      :disabled="isLoading"
                    />
                  </div>
                </div>
              </div>

              <b-row class="mb-2">
                <b-col cols="12">
                  <b-form-checkbox
                    size="sm"
                    data-testid="mfa-trust-device"
                    v-model="trustDevice"
                    :disabled="isLoading"
                    >จำอุปกรณ์นี้ไว้ 30 วัน</b-form-checkbox
                  >
                </b-col>
              </b-row>

              <div class="row">
                <div class="col-12">
                  <button
                    type="submit"
                    data-testid="mfa-submit"
                    class="btn btn-primary btn-block btn-sm"
                    :disabled="
                      isLoading ||
                      (recoveryMode ? !recoveryCode : mfaCode.length !== 6)
                    "
                  >
                    {{ isLoading ? "กำลังตรวจสอบ..." : "ยืนยัน" }}
                  </button>
                </div>
              </div>
            </b-form>

            <div class="row">
              <div class="col-12 text-center mt-2">
                <a
                  href="#"
                  data-testid="mfa-use-recovery"
                  @click.prevent="toggleRecoveryMode"
                  >{{
                    recoveryMode ? "ใช้รหัส 6 หลักแทน" : "ใช้รหัสสำรองแทน"
                  }}</a
                >
              </div>
            </div>

            <div class="row">
              <div class="col-12 text-center mt-2">
                <a
                  href="#"
                  data-testid="mfa-back"
                  @click.prevent="backToPassword"
                  >ย้อนกลับ</a
                >
              </div>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </div>
    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import { eventBus } from "@/main";
import { ERROR_MESSAGES } from "@/constants/errorMessages";
import { isPasskeySupported, startAuthentication } from "@/utils/webauthn";

// A genuine user cancel takes at least a moment: the prompt has to render
// and the user has to dismiss it. A lost-transient-activation failure (the
// network round trip before the ceremony ate WebKit's user-gesture window)
// comes back almost immediately instead. Below this threshold we treat the
// "cancelled" result as "the prompt never appeared" and say so.
const PASSKEY_LOST_ACTIVATION_THRESHOLD_MS = 400;

export default {
  name: "Login",
  components: {
    Loading,
    Dialog,
  },
  data() {
    return {
      isLoading: false,
      loginForm: {
        username: "",
        password: "",
        rememberMe: false,
      },
      statusLogin: false,
      loginErrorMessage: "",
      showPassword: false,
      ERROR_MESSAGES, // Make error messages available in template
      // MFA challenge state
      mfaToken: null,
      mfaMethods: [],
      mfaCode: "",
      recoveryMode: false,
      recoveryCode: "",
      trustDevice: false,
      // passkey: capability is resolved once here, never in the template
      passkeySupported: false,
      isPasskeyLoading: false,
    };
  },
  created() {
    this.fetchAppVersion();
    this.loadFromLocalStorage();
    this.passkeySupported = isPasskeySupported();
  },
  beforeDestroy() {
    // Clear sensitive data
    this.loginForm.password = "";
  },
  computed: {
    ...mapGetters({
      appVersion: "moduleMaster/getAppVersion",
      getLoginApiStatus: "moduleAuth/getLoginApiStatus",
    }),
  },
  methods: {
    ...mapActions({
      login: "moduleUser/login",
      fetchAppVersion: "moduleMaster/fetchAppVersion",
      actionLoginApi: "moduleAuth/loginApi",
      actionLoginMfa: "moduleAuth/loginMfa",
      actionPasskeyLoginStart: "moduleAuth/passkeyLoginStart",
      actionPasskeyLoginFinish: "moduleAuth/passkeyLoginFinish",
    }),

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    async submit() {
      this.isLoading = true;
      this.statusLogin = false;
      this.loginErrorMessage = "";
      try {
        const loginData = {
          username: this.loginForm.username.trim(),
          password: this.loginForm.password.trim(),
          rememberMe: this.loginForm.rememberMe,
        };

        const result = await this.actionLoginApi(loginData);

        if (result.outcome === "success") {
          // Clear sensitive data
          this.loginForm.password = "";
          await this.redirectAfterLogin();
        } else if (result.outcome === "mfa_required") {
          // Clear sensitive data; hand off to the code-challenge state
          this.loginForm.password = "";
          this.mfaToken = result.mfaToken;
          this.mfaMethods = result.methods || [];
          this.focusMfaCodeInput();
        } else if (result.outcome === "locked") {
          this.loginErrorMessage =
            "บัญชีถูกล็อกชั่วคราวเนื่องจากกรอกรหัสผ่านผิดหลายครั้ง กรุณาลองใหม่ภายหลัง";
          this.statusLogin = true;
        }
      } catch (err) {
        const statusCode = err.response?.data?.errorCode;

        if (statusCode === 401) {
          // Handle authentication errors locally (show inline warning)
          this.loginForm.password = "";
          this.statusLogin = true;
        } else {
          // Use global error system for all other errors (network, server, etc.)
          eventBus.$emit("apiError", err);
        }
      } finally {
        this.isLoading = false;
      }
    },

    // Passwordless login: start -> the browser's own passkey prompt -> finish.
    // The finish action routes through the same applyLoginSuccess helper the
    // password login uses, so the success path here is just redirectAfterLogin.
    async submitPasskeyLogin() {
      this.isPasskeyLoading = true;
      this.statusLogin = false;
      this.loginErrorMessage = "";
      let startedAt;
      try {
        const started = await this.actionPasskeyLoginStart();

        // The platform decides what to offer here: Touch ID, Windows Hello, or
        // the cross-device QR ("use a phone"). We never restrict the
        // authenticator attachment, so all three stay available.
        startedAt = Date.now();
        const response = await startAuthentication(started.options);

        const result = await this.actionPasskeyLoginFinish({
          response,
          authenticationToken: started.authenticationToken,
          rememberMe: this.loginForm.rememberMe,
        });

        if (result.outcome === "success") {
          // Clear sensitive data
          this.loginForm.password = "";
          await this.redirectAfterLogin();
        }
      } catch (err) {
        if (err && err.cancelled) {
          if (Date.now() - startedAt < PASSKEY_LOST_ACTIVATION_THRESHOLD_MS) {
            // Came back too fast to be a real cancel: the browser most likely
            // never showed the prompt at all (WebKit lost transient user
            // activation during the network round trip beforehand). Say so
            // instead of silently doing nothing.
            this.loginErrorMessage =
              "ไม่สามารถเปิดหน้าต่าง Passkey ได้ กรุณาลองใหม่อีกครั้ง";
            this.statusLogin = true;
            return;
          }
          // The user dismissed the browser prompt -- not an error. Say nothing
          // and leave them on the form.
          return;
        }

        const status = err.response?.status || err.response?.data?.errorCode;

        if (status === 401) {
          // Generic failure by design (unknown credential vs bad assertion are
          // indistinguishable on the wire): one message covers both.
          this.loginErrorMessage =
            "ไม่สามารถเข้าสู่ระบบด้วย Passkey ได้ กรุณาลองใหม่หรือใช้รหัสผ่าน";
          this.statusLogin = true;
        } else if (status === 501) {
          // Passkey is gated off for this tenant: drop the control for the rest
          // of the session and fall back silently to the password form that is
          // already on screen.
          this.passkeySupported = false;
        } else {
          // Use global error system for all other errors (network, server, etc.)
          eventBus.$emit("apiError", err);
        }
      } finally {
        this.isPasskeyLoading = false;
      }
    },

    // Shared by the password-success path and the MFA-success path so the
    // two redirect flows cannot drift.
    redirectAfterLogin() {
      // Redirect with priority: query.from > localStorage > default
      let redirectPath = "/clinic";

      if (this.$route.query.from) {
        // Priority 1: Redirect from axios interceptor
        redirectPath = this.$route.query.from;
      } else if (this.loginForm.rememberMe) {
        // Priority 2: Saved route from localStorage (only if rememberMe is enabled)
        const savedRoute = localStorage.getItem("lastAuthenticatedRoute");
        if (savedRoute) {
          redirectPath = savedRoute;
          // Clear saved route after using it
          localStorage.removeItem("lastAuthenticatedRoute");
        }
      }

      return this.$router.replace({ path: redirectPath });
    },

    async handleMfaSubmit() {
      if (this.recoveryMode) {
        await this.submitRecoveryCode();
      } else {
        await this.submitMfaCode();
      }
    },

    async submitMfaCode() {
      this.isLoading = true;
      this.statusLogin = false;
      this.loginErrorMessage = "";
      try {
        const result = await this.actionLoginMfa({
          mfaToken: this.mfaToken,
          code: this.mfaCode,
          trustDevice: this.trustDevice,
        });

        if (result.outcome === "success") {
          await this.redirectAfterLogin();
        }
      } catch (err) {
        this.handleMfaError(err);
      } finally {
        this.isLoading = false;
      }
    },

    async submitRecoveryCode() {
      this.isLoading = true;
      this.statusLogin = false;
      this.loginErrorMessage = "";
      try {
        const result = await this.actionLoginMfa({
          mfaToken: this.mfaToken,
          recoveryCode: this.recoveryCode,
          trustDevice: this.trustDevice,
        });

        if (result.outcome === "success") {
          await this.redirectAfterLogin();
        }
      } catch (err) {
        this.handleMfaError(err);
      } finally {
        this.isLoading = false;
      }
    },

    handleMfaError(err) {
      const status = err.response?.status;

      if (status === 401) {
        this.loginErrorMessage = "รหัสไม่ถูกต้อง กรุณาลองใหม่";
        this.statusLogin = true;
        this.mfaCode = "";
        this.recoveryCode = "";
      } else if (status === 423) {
        this.loginErrorMessage =
          "ยืนยันตัวตนล้มเหลวหลายครั้ง กรุณาลองใหม่ภายหลัง";
        this.statusLogin = true;
      } else {
        eventBus.$emit("apiError", err);
      }
    },

    toggleRecoveryMode() {
      this.recoveryMode = !this.recoveryMode;
      this.statusLogin = false;
      this.loginErrorMessage = "";
      this.mfaCode = "";
      this.recoveryCode = "";

      if (!this.recoveryMode) {
        this.focusMfaCodeInput();
      }
    },

    backToPassword() {
      this.mfaToken = null;
      this.mfaMethods = [];
      this.mfaCode = "";
      this.recoveryCode = "";
      this.recoveryMode = false;
      this.trustDevice = false;
      this.statusLogin = false;
      this.loginErrorMessage = "";
    },

    onMfaCodeInput(value) {
      this.mfaCode = (value || "").replace(/\D/g, "").slice(0, 6);
    },

    focusMfaCodeInput() {
      this.$nextTick(() => {
        if (this.$refs.mfaCodeInput) {
          this.$refs.mfaCodeInput.focus();
        }
      });
    },

    setStatusLogin() {
      this.statusLogin = false;
    },

    saveToLocalStorage(value) {
      localStorage.setItem("rememberMe", value);

      // Clear saved route when Remember Me is unchecked
      if (!value) {
        localStorage.removeItem("lastAuthenticatedRoute");
      }
    },

    loadFromLocalStorage() {
      this.loginForm.rememberMe = Boolean(
        JSON.parse(localStorage.getItem("rememberMe"))
      );
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
  },
};
</script>

<style>
.cursor-pointer {
  cursor: pointer;
}

/* "หรือ" separator between the passkey button and the password form */
.passkey-divider {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.8rem;
}
.passkey-divider::before,
.passkey-divider::after {
  content: "";
  flex: 1;
  border-top: 1px solid #dee2e6;
}
.passkey-divider span {
  padding: 0 0.75rem;
}
</style>